import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import Alert from "../../components/Alert";
import Link from "../../components/Link";
import Save from "../../../icons/Save";
import {
  useSpecificationState,
  useSpecificationStateStore,
} from "./specificationState";
import { useUpdateCondition } from "../../../hooks/mutations/useSpecMutations";
import {
  useGetConditionModification,
  useGetConditionsFromExp,
  useItemValidation,
  useGetItemsConstraints,
  useGetWorkOrdersFromExp,
} from "../../../hooks/queries/useSpecQueries";
import {
  useValidationStateStore,
  normalizeServerViolations,
} from "./validationState";
import { QueryKey } from "../../../utils/api";
import BOMView from "./BOMView";
import GridView from "./GridView";
import StatusView from "./StatusView";
import AddNewConditionModal from "./AddNewConditionModal";
import { useBulkEditStateStore } from "./bulkEditState";
import { isConditionEditLocked } from "./statusState";
import BulkDiffModal from "./BulkDiffModal";
import client from "../../../api";

type BulkUpdateResult = {
  cellConditionId: number | null;
  status: "succeeded" | "failed" | "skipped";
  data?: {
    uuid: string;
    external_spec_id: string;
  };
  reason?: string;
  error?: {
    status_code?: number;
    detail?: string;
    payload?: unknown;
  };
};

type BulkUpdateResponse = {
  summary: {
    total: number;
    succeeded: number;
    failed: number;
    skipped: number;
  };
  results: BulkUpdateResult[];
};

type BulkUpdateTaskResponse = {
  task_id: string;
  status: "queued" | "pending" | "running" | "succeeded" | "failed" | string;
  result?: BulkUpdateResponse;
  error?: string;
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  backgroundColor: "#f5f5f5",
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  backgroundColor: "#fff",
  borderBottom: "1px solid #e0e0e0",
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  fontSize: "16px",
}));

const HeaderRight = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
}));

const ViewContainer = styled(Box)({
  flex: 1,
  display: "flex",
  width: "100%",
  minWidth: 0,
});

const StyledTab = styled(Tab)({
  fontWeight: "unset !important",
  padding: 0,
  minHeight: "40px",
  height: "40px",
});

const parseViewParam = (
  value: string | null
): "bom" | "grid" | "status" | null => {
  if (value === "bom" || value === "grid" || value === "status") {
    return value;
  }

  return null;
};

const Specification = () => {
  const { exp_id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = parseViewParam(searchParams.get("view"));
  const activeView: "bom" | "grid" | "status" = viewParam || "bom";
  const [isAddConditionModalOpen, setIsAddConditionModalOpen] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date>(new Date());
  const [hasSaved, setHasSaved] = useState<boolean>(false);
  const [timestampTrigger, setTimestampTrigger] = useState<number>(0);

  useEffect(() => {
    if (viewParam) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("view", activeView);
    setSearchParams(newSearchParams, { replace: true });
  }, [viewParam, activeView, searchParams, setSearchParams]);

  // Get condition from state
  const condition = useSpecificationStateStore((state) => state.condition);
  const activeModifyConditionId = useSpecificationStateStore(
    (state) => state.activeModifyConditionId
  );
  const modifyStatus = useSpecificationStateStore(
    (state) => state.modifyStatus
  );
  const setModifyState = useSpecificationStateStore(
    (state) => state.setModifyState
  );
  const setCondition = useSpecificationStateStore(
    (state) => state.setCondition
  );
  const setConstraints = useValidationStateStore(
    (state) => state.setConstraints
  );
  const setConstraintViolations = useValidationStateStore(
    (state) => state.setConstraintViolations
  );
  const upsertBulkBaseline = useBulkEditStateStore(
    (state) => state.upsertBaseline
  );
  const cellChangesByKey = useBulkEditStateStore(
    (state) => state.cellChangesByKey
  );
  const buildBulkUpdatedConditionsForSave = useBulkEditStateStore(
    (state) => state.buildUpdatedConditionsForSave
  );
  const clearBulkChanges = useBulkEditStateStore((state) => state.clearChanges);
  const clearBulkChangesForConditionIds = useBulkEditStateStore(
    (state) => state.clearChangesForConditionIds
  );
  const bulkSaveTaskId = useBulkEditStateStore((state) => state.bulkSaveTaskId);
  const startPendingSave = useBulkEditStateStore(
    (state) => state.startPendingSave
  );
  const finishPendingSave = useBulkEditStateStore(
    (state) => state.finishPendingSave
  );
  const clearPendingSave = useBulkEditStateStore(
    (state) => state.clearPendingSave
  );
  const [isBulkSaveReviewOpen, setIsBulkSaveReviewOpen] = useState(false);
  const isBulkSaving = Boolean(bulkSaveTaskId);

  useEffect(() => {
    setModifyState(null, null);
  }, [exp_id, setModifyState]);

  // Use item changes hook for BOM view state and notifications
  const {
    hasUnsavedChanges,
    clearChanges,
    successMessage,
    errorMessage,
    setSuccessMessage,
    clearSuccessMessage,
    setErrorMessage,
    clearErrorMessage,
  } = useSpecificationState(null);
  // Derived from a reactive slice — recomputes whenever cellChangesByKey changes.
  const bulkDiffChangeCount = useMemo(
    () => Object.keys(cellChangesByKey).length,
    [cellChangesByKey]
  );
  const hasBulkUnsavedChanges = bulkDiffChangeCount > 0;
  const hasAnyUnsavedChanges = hasUnsavedChanges || hasBulkUnsavedChanges;
  const bulkDiffSummary = useMemo(
    () => useBulkEditStateStore.getState().getDiffSummary(),
    // eslint-disable-next-line
    [cellChangesByKey]
  );

  // Get conditions data to map item type names to item UUIDs
  const { data: conditions, isLoading: conditionsLoading } =
    useGetConditionsFromExp();

  // Fetch work orders so we can derive which conditions have already been
  // submitted (or are mid-modification). The resulting Set is the source of
  // truth shared by GridView and StatusView so the two views stay in sync
  // about which conditions are locked from editing.
  const { data: workOrders } = useGetWorkOrdersFromExp();

  const submittedConditionIds = useMemo(() => {
    const ids = new Set<number>();
    if (!workOrders) {
      return ids;
    }
    Object.values(
      workOrders as Record<string, ConditionWorkOrderGroupResponse>
    ).forEach((group) => {
      if (group?.conditions) {
        Object.keys(group.conditions).forEach((conditionId) => {
          ids.add(Number(conditionId));
        });
      }
    });
    return ids;
  }, [workOrders]);

  useEffect(() => {
    if (!conditions || conditions.length === 0) {
      return;
    }

    if (hasBulkUnsavedChanges) {
      return;
    }

    upsertBulkBaseline(conditions);
  }, [conditions, upsertBulkBaseline, hasBulkUnsavedChanges]);

  const checkUnsavedChangesBeforeNavigation = (): boolean => {
    if (!hasAnyUnsavedChanges) {
      return true;
    }

    const proceed = window.confirm(
      "You have unsaved changes. Changing views or leaving this page will remove all in progress edits. Do you want to proceed?"
    );

    if (proceed) {
      clearChanges();
      clearBulkChanges();
      setIsBulkSaveReviewOpen(false);
    }

    return proceed;
  };

  useGetConditionModification(undefined, {
    onSuccess: (data) => {
      if (!data) {
        setModifyState(null, null);
        return;
      }
      setModifyState(data.cell_condition_id, data.status);
    },
  });

  const modifyConditionName = (conditionId: number) =>
    conditions?.find(
      (conditionItem) => conditionItem.cell_condition_id === conditionId
    )?.name ?? `Condition ${conditionId}`;

  useGetConditionModification(activeModifyConditionId, {
    enabled: activeModifyConditionId !== null && modifyStatus === "queued",
    refetchInterval: 2000,
    staleTime: 0,
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (data.status === "submitted") {
        setModifyState(null, null);
        queryClient.removeQueries({
          queryKey: [
            QueryKey.WorkOrders,
            exp_id,
            "modify",
            data.cell_condition_id,
          ],
          exact: true,
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKey.WorkOrders, exp_id],
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKey.WorkOrders, exp_id, "modify"],
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKey.Conditions, exp_id],
          }),
          queryClient.invalidateQueries({ queryKey: [QueryKey.Items] }),
        ]);
        setSuccessMessage(
          `${modifyConditionName(
            data.cell_condition_id
          )} resubmitted successfully.`
        );
        return;
      }
      if (data.status === "failed") {
        setModifyState(data.cell_condition_id, data.status);
        setErrorMessage(
          `${modifyConditionName(
            data.cell_condition_id
          )} could not be resubmitted. Retry or cancel the modification.`
        );
        return;
      }
      setModifyState(data.cell_condition_id, data.status);
    },
  });

  const itemUuid =
    condition &&
    "uuid" in condition.item &&
    typeof condition.item.uuid === "string"
      ? condition.item.uuid
      : condition?.external_spec_id;

  const loadedConstraintsForItemRef = useRef<string | null>(null);
  const { constraints: constraintsArray, queries: constraintQueries } =
    useGetItemsConstraints(itemUuid ? [itemUuid] : []);
  const constraints = constraintsArray[0];
  const constraintsFetched =
    Boolean(itemUuid) && constraintQueries[0]?.isFetched;

  useEffect(() => {
    if (!constraintsFetched || !itemUuid) {
      return;
    }

    const shouldSetConstraints =
      loadedConstraintsForItemRef.current !== itemUuid;
    if (!shouldSetConstraints) {
      return;
    }

    loadedConstraintsForItemRef.current = itemUuid;
    setConstraints(constraints || null);
  }, [constraints, constraintsFetched, itemUuid, setConstraints]);

  const { data: activeValidation, error: validationError } = useItemValidation(
    itemUuid,
    {
      enabled: Boolean(itemUuid),
    }
  );

  // When the on-demand validation result for the active condition arrives
  // (or is read from cache), seed the validation store. This is the only
  // place where on-demand validation flows into the in-memory store; all
  // other writes come from single-save responses.
  useEffect(() => {
    if (!constraintsFetched || !condition || !activeValidation) {
      return;
    }

    const serverViolations = activeValidation.violations?.length
      ? normalizeServerViolations(
          activeValidation.violations,
          condition,
          constraints || null
        )
      : [];

    setConstraintViolations(serverViolations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValidation, constraintsFetched, condition?.external_spec_id]);

  // Surface validation fetch errors as a non-blocking toast so the user
  // knows constraint warnings may be stale. The item itself still loads.
  const lastReportedValidationErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!validationError) {
      lastReportedValidationErrorRef.current = null;
      return;
    }
    const message = validationError.message || "Failed to load validation.";
    if (lastReportedValidationErrorRef.current === message) {
      return;
    }
    lastReportedValidationErrorRef.current = message;
    setErrorMessage(`Validation unavailable: ${message}`);
  }, [validationError, setErrorMessage]);

  const handleMutationError = (error: unknown) => {
    console.error("Save failed:", error);

    const fallbackMessage = "Failed to save changes. Please try again.";
    const errorPayload =
      typeof error === "object" && error !== null && "payload" in error
        ? (error as { payload?: unknown }).payload
        : undefined;
    const errorMessage =
      error instanceof Error && error.message
        ? error.message
        : typeof error === "string"
        ? error
        : fallbackMessage;
    const payloadDetail =
      errorPayload && typeof errorPayload === "object"
        ? String(
            (
              errorPayload as {
                detail?: unknown;
                description?: unknown;
                title?: unknown;
              }
            ).detail ??
              (
                errorPayload as {
                  detail?: unknown;
                  description?: unknown;
                  title?: unknown;
                }
              ).description ??
              (
                errorPayload as {
                  detail?: unknown;
                  description?: unknown;
                  title?: unknown;
                }
              ).title ??
              ""
          )
        : "";
    const initialMessage = payloadDetail || errorMessage || fallbackMessage;

    let derivedMessage = initialMessage;
    const isValidationError = initialMessage.startsWith(
      "Template validation failed"
    );
    if (isValidationError) {
      derivedMessage = "Template validation failed, please resolve all errors.";
    }
    const violations =
      errorPayload &&
      typeof errorPayload === "object" &&
      "violations" in errorPayload
        ? (errorPayload as { violations?: ConstraintViolation[] }).violations
        : undefined;

    if (isValidationError && condition && Array.isArray(violations)) {
      const serverViolations = normalizeServerViolations(
        violations,
        condition,
        constraints || null
      );

      queryClient.setQueryData<TemplateValidationResult>(
        [QueryKey.Items, condition.external_spec_id, "validation"],
        { violations }
      );

      setConstraintViolations(serverViolations);
    } else if (condition) {
      // Trigger a refetch of the validation query
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Items, condition.external_spec_id, "validation"],
      });
    }
    setErrorMessage(derivedMessage || fallbackMessage);
  };

  const cacheValidationResult = (
    updatedCondition: FormspecCondition & {
      validation?: TemplateValidationResult | null;
    }
  ) => {
    if (!updatedCondition.external_spec_id || !updatedCondition.validation) {
      return;
    }

    queryClient.setQueryData<TemplateValidationResult>(
      [QueryKey.Items, updatedCondition.external_spec_id, "validation"],
      updatedCondition.validation
    );
  };

  // Mutation hook for updating conditions
  const { mutateAsync, isLoading } = useUpdateCondition({
    onSuccess: async (response) => {
      const freshCondition: FormspecCondition = response as FormspecCondition;

      // Update the local conditions cache with the full fresh condition tree
      // to avoid stale data being shown and saved
      queryClient.setQueryData<FormspecCondition[]>(
        [QueryKey.Conditions, exp_id],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((c) =>
            c.cell_condition_id === response.cell_condition_id
              ? freshCondition
              : c
          );
        }
      );

      // setCondition calls initializeItemWithBOM internally, which zeroes all
      // *_changes arrays on every node — so clearChanges() below is redundant
      // but kept as a safety net.
      setCondition(freshCondition);

      const responseWithValidation = response as FormspecCondition & {
        validation?: TemplateValidationResult | null;
      };
      cacheValidationResult(responseWithValidation);
      if (responseWithValidation.validation) {
        setConstraintViolations(
          response.validation.violations?.length
            ? normalizeServerViolations(
                response.validation.violations,
                freshCondition,
                constraints || null
              )
            : []
        );
      }

      clearChanges();
      setSuccessMessage("Changes saved successfully!");
      setLastSavedTime(new Date());
      setHasSaved(true);
    },
    onError: handleMutationError,
  });

  // Add beforeunload warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasAnyUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasAnyUnsavedChanges]);

  // Update timestamp display every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setTimestampTrigger((prev) => prev + 1);
    }, 60000); // 60 seconds

    return () => clearInterval(timer);
  }, []);

  const handleOakClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!checkUnsavedChangesBeforeNavigation()) {
      return;
    }
    navigate("/experiments");
  };

  const handleExpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!checkUnsavedChangesBeforeNavigation()) {
      return;
    }
    if (!exp_id) return;
    navigate(`/experiments/${exp_id}`);
  };

  const formatTimestamp = useMemo(() => {
    const now = new Date();
    const diffMs = now.getTime() - lastSavedTime.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    let relativeTime = "";
    if (diffSeconds < 60) {
      relativeTime = `${diffSeconds} second${diffSeconds !== 1 ? "s" : ""} ago`;
    } else if (diffMinutes < 60) {
      relativeTime = `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    } else {
      relativeTime = `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    }

    const hours = lastSavedTime.getHours();
    const minutes = lastSavedTime.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    const absoluteTime = `${displayHours}:${minutes}${ampm}`;

    const prefix = `Last ${hasSaved ? "saved" : "refreshed"}:`;
    return `${prefix} ${relativeTime} (${absoluteTime})`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedTime, hasSaved, timestampTrigger]);

  const handleViewChange = (
    _: React.SyntheticEvent,
    newValue: "bom" | "grid" | "status"
  ) => {
    if (checkUnsavedChangesBeforeNavigation()) {
      if (newValue === activeView) {
        return;
      }

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("view", newValue);
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  // While a condition is being modified, only that condition is editable.
  const isModifyReadOnly =
    activeModifyConditionId !== null &&
    condition?.cell_condition_id !== activeModifyConditionId;
  const isModificationProcessing = modifyStatus === "queued";
  // BOM edits additionally lock submitted conditions until they're modified,
  // matching GridView's per-row lock.
  const isBomReadOnly =
    !!condition &&
    isConditionEditLocked(
      condition.cell_condition_id,
      activeModifyConditionId,
      submittedConditionIds
    );

  const handleSave = async () => {
    console.log("Saving changes...");

    if (isModificationProcessing) {
      setErrorMessage("Wait for the condition resubmission to finish.");
      return;
    }

    if (!conditions || conditions.length === 0) {
      setErrorMessage("No conditions available");
      return;
    }

    if (isModifyReadOnly) {
      setErrorMessage("Only the condition being modified can be changed.");
      return;
    }

    if (activeView === "grid") {
      if (!hasBulkUnsavedChanges) {
        setErrorMessage("No grid changes to save.");
        return;
      }

      setIsBulkSaveReviewOpen(true);
      return;
    }

    if (isBomReadOnly) {
      setErrorMessage("Submitted conditions must be modified before editing.");
      return;
    }

    // Get the active condition from state
    if (!condition) {
      setErrorMessage("No active condition");
      return;
    }
    const activeItemUuid = condition.external_spec_id;

    try {
      // Send the full condition tree with inline changes
      await mutateAsync({
        item_uuid: activeItemUuid,
        metadata: {
          cellConditionId: condition.cell_condition_id,
          item_tree: condition,
        },
      });
    } catch (_error) {
      // Error handling is centralized in the mutation's onError callback.
    }
  };

  const handleAddConditionSubmit = (data: NewConditionData) => {
    if (!data?.createdConditionId) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("condition", String(data.createdConditionId));
    setSearchParams(newSearchParams, { replace: true });
  };

  const applyBulkUpdateResult = async (response: BulkUpdateResponse) => {
    const successfulResults = (response.results || []).filter(
      (result) => result.status === "succeeded" && result.data
    );
    const successfulConditionIds = successfulResults
      .map((result) => result.cellConditionId)
      .filter((id): id is number => typeof id === "number");

    const firstFailure = (response.results || []).find(
      (result) => result.status === "failed"
    );

    // Bulk save returns minimal per-row data ({uuid, external_spec_id}) by
    // design — the worker uses include_bom=False for performance, so the
    // response cannot be used to repopulate the conditions list. Instead we
    // invalidate the conditions query (the source of truth for the grid /
    // BOM view) plus every affected item's per-uuid cache (details,
    // constraints, validation), then await the conditions refetch so the
    // pending-row spinner is cleared only once authoritative data has
    // landed.
    const invalidatedSpecIds = new Set<string>();
    successfulResults.forEach((result) => {
      if (!result.data) {
        return;
      }
      invalidatedSpecIds.add(result.data.external_spec_id);
    });

    const conditionsBeforeRefetch =
      queryClient.getQueryData<FormspecCondition[]>([
        QueryKey.Conditions,
        exp_id,
      ]) || [];
    conditionsBeforeRefetch.forEach((existing) => {
      if (
        typeof existing.cell_condition_id === "number" &&
        successfulConditionIds.includes(existing.cell_condition_id) &&
        existing.external_spec_id
      ) {
        // Also invalidate the previous-version item caches so anything
        // still keyed on the old external_spec_id refetches on next view.
        invalidatedSpecIds.add(existing.external_spec_id);
      }
    });

    invalidatedSpecIds.forEach((specId) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Items, specId],
      });
    });

    const { succeeded, failed, skipped } = response.summary || {
      succeeded: successfulResults.length,
      failed: 0,
      skipped: 0,
    };

    if (failed === 0) {
      setIsBulkSaveReviewOpen(false);
    }

    if (failed > 0) {
      const failureDetail = firstFailure?.error?.detail;
      const baseMessage = `${succeeded} condition(s) saved, ${failed} failed${
        skipped > 0 ? `, ${skipped} skipped` : ""
      }.`;
      setErrorMessage(
        failureDetail
          ? `${baseMessage} First error: ${failureDetail}`
          : baseMessage
      );
    } else {
      const successMessage = `${succeeded} condition(s) saved${
        skipped > 0 ? `, ${skipped} skipped` : ""
      }.`;
      setSuccessMessage(successMessage);
    }

    if (succeeded > 0) {
      setLastSavedTime(new Date());
      setHasSaved(true);
    }

    // Force-refetch the conditions list so the grid / BOM view see the
    // newly-saved values. Awaited so callers (the bulk poll effect) only
    // clear pending state after fresh data has arrived.
    await queryClient.refetchQueries({
      queryKey: [QueryKey.Conditions, exp_id],
    });

    // Clear pending cell changes only after the refetch lands so the
    // overlay keeps masking baseRows until baseRows itself reflects the
    // saved values. This prevents a brief flash back to the pre-edit
    // value during the refetch window.
    if (successfulConditionIds.length > 0) {
      clearBulkChangesForConditionIds(successfulConditionIds);
    }
  };

  const handleBulkSaveConfirm = async () => {
    const pendingConditions = buildBulkUpdatedConditionsForSave();

    if (pendingConditions.length === 0) {
      setErrorMessage("No valid grid changes were found to save.");
      setIsBulkSaveReviewOpen(false);
      return;
    }

    const pendingConditionIds = pendingConditions
      .map((updatedCondition) => updatedCondition.cell_condition_id)
      .filter((id): id is number => typeof id === "number");

    try {
      const response = (await client.post("formspec/items/bulk", {
        updates: pendingConditions.map((updatedCondition) => ({
          cellConditionId: updatedCondition.cell_condition_id,
          item_uuid: updatedCondition.external_spec_id,
          item_tree: updatedCondition,
        })),
      })) as BulkUpdateTaskResponse;

      if (!response.task_id) {
        throw new Error("Bulk save did not return a task ID.");
      }

      startPendingSave(response.task_id, pendingConditionIds);
      setSuccessMessage(
        "Bulk save started. Conditions will update when processing completes."
      );
    } catch (error) {
      handleMutationError(error);
    }
  };

  useEffect(() => {
    if (!bulkSaveTaskId) {
      return undefined;
    }

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const response = (await client.get(
          `formspec/items/bulk/${bulkSaveTaskId}`
        )) as BulkUpdateTaskResponse;

        if (isCancelled) {
          return;
        }

        if (response.status === "succeeded" && response.result) {
          await applyBulkUpdateResult(response.result);
          if (isCancelled) {
            return;
          }
          finishPendingSave();
          return;
        }

        if (response.status === "failed") {
          clearPendingSave();
          setErrorMessage(
            response.error ||
              "Bulk save failed. Your unsaved changes were preserved."
          );
          return;
        }

        timeoutId = setTimeout(poll, 2000);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        clearPendingSave();
        handleMutationError(error);
      }
    };

    poll();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // applyBulkUpdateResult closes over fresh UI state; polling should restart
    // only when the task itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSaveTaskId]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Link href="/" onClick={handleOakClick}>
            Oak
          </Link>
          <Box component="span" sx={{ color: "#999" }}>
            {" "}
            /{" "}
          </Box>
          <Link href={`/experiments/${exp_id}`} onClick={handleExpClick}>
            EXP {exp_id}
          </Link>
          <Box component="span" sx={{ color: "#999" }}>
            {" "}
            /{" "}
          </Box>
          <Box component="span">Conditions</Box>
          {formatTimestamp && (
            <Box component="span" sx={{ color: "#999", fontSize: "12px" }}>
              {formatTimestamp}
            </Box>
          )}
        </HeaderLeft>
        <HeaderRight>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsAddConditionModalOpen(true)}
            startIcon={
              <span aria-hidden="true" style={{ lineHeight: "1" }}>
                +
              </span>
            }
            disabled={activeModifyConditionId !== null}
            sx={{
              width: "fit-content",
              display: "flex",
              alignItems: "center",
              color: "#2E7D32",
              borderColor: "#2E7D32",
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "uppercase",
              padding: "4px 10px",
              "&:hover": {
                borderColor: "#2E7D32",
                backgroundColor: "rgba(46, 125, 50, 0.04)",
              },
              "&.Mui-disabled": {
                color: "rgba(46, 125, 50, 0.4)",
                borderColor: "rgba(46, 125, 50, 0.4)",
              },
            }}
          >
            ADD CONDITION
          </Button>

          <Tooltip
            title={!hasAnyUnsavedChanges ? "No changes to save" : ""}
            placement="bottom"
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSave}
                disabled={
                  (!hasUnsavedChanges && !hasBulkUnsavedChanges) ||
                  isLoading ||
                  isModifyReadOnly ||
                  isModificationProcessing ||
                  (activeView === "bom" && isBomReadOnly)
                }
                startIcon={!isLoading ? <Save fill="#1976D2" /> : null}
                endIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  width: "fit-content",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1976D2",
                  borderColor: "#1976D2",
                  padding: "4px 10px",
                  textTransform: "uppercase",
                  "&:hover": {
                    borderColor: "#1976D2",
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(25, 118, 210, 0.4)",
                    borderColor: "rgba(25, 118, 210, 0.4)",
                  },
                }}
              >
                SAVE CONDITIONS
              </Button>
            </span>
          </Tooltip>
        </HeaderRight>
      </Header>

      <Box
        style={{
          backgroundColor: "#fafafa",
          height: "fit-content",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Paper square elevation={0}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "#fff",
            }}
          >
            <Tabs
              value={activeView}
              onChange={handleViewChange}
              aria-label="view selection tabs"
              variant="fullWidth"
              sx={{
                minHeight: "40px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "16px",
                  color: "#999",
                },
                "& .Mui-selected": {
                  color: "#1976d2 !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <StyledTab label="Edit Condition" value="bom" />
              <StyledTab label="Compare Conditions" value="grid" />
              <StyledTab label="View Conditions Statuses" value="status" />
            </Tabs>
          </Box>
        </Paper>

        <ViewContainer>
          {conditionsLoading ? (
            <CircularProgress style={{ margin: "20px" }} />
          ) : activeView === "bom" ? (
            <BOMView
              onOpenAddCondition={() => setIsAddConditionModalOpen(true)}
              readOnly={isBomReadOnly || isModificationProcessing}
              duplicateDisabled={isModifyReadOnly || isModificationProcessing}
              constraints={constraints || null}
              constraintsFetched={constraintsFetched}
            />
          ) : activeView === "grid" ? (
            <GridView
              checkUnsavedChangesBeforeNavigation={
                checkUnsavedChangesBeforeNavigation
              }
              submittedConditionIds={submittedConditionIds}
            />
          ) : (
            <StatusView submittedConditionIds={submittedConditionIds} />
          )}
        </ViewContainer>
      </Box>

      {successMessage && (
        <Alert open severity="success" onClose={clearSuccessMessage}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert
          open
          severity="error"
          onClose={clearErrorMessage}
          autoHideDuration={null}
        >
          {errorMessage}
        </Alert>
      )}

      <AddNewConditionModal
        open={isAddConditionModalOpen}
        onClose={() => setIsAddConditionModalOpen(false)}
        onSubmit={handleAddConditionSubmit}
      />

      <BulkDiffModal
        open={isBulkSaveReviewOpen}
        onClose={() => setIsBulkSaveReviewOpen(false)}
        onConfirm={handleBulkSaveConfirm}
        diffSummary={bulkDiffSummary}
        changeCount={bulkDiffChangeCount}
        isSaving={isLoading || isBulkSaving}
      />
    </Container>
  );
};

export default Specification;
